import { ReactNode } from "react";
import { Box, Icon, Tooltip, Typography } from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";
import { SB, labelSx, tabularSx } from "./scenarioBar/tokens";

type ScenarioSegmentButtonProps = {
  /** Small-caps label above the value ("Strategy", "Ignitions"). */
  label: string;
  /** Material icon for the segment's tile. */
  icon: string;
  /** The current selection, shown in bold. */
  value: string;
  /** Secondary figure after the value ("103.7 ha", "4 ignitions"). */
  meta?: ReactNode;
  /** Hover text; defaults to the value so a truncated name can still be read. */
  tooltip?: string;
  /** Tints the segment and flips the chevron while its popover is open. */
  open?: boolean;
  disabled?: boolean;
  /** 0–1: draws a thin progress line along the bottom (strategy computing). */
  progress?: number | null;
  maxValueWidth?: number;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
};

/**
 * One segment of the Strategy › Treatments › Ignitions scenario bar: icon
 * tile, label, value + meta, chevron. Neutral at rest; teal tint and ring only
 * while its popover is open, so "open" is never confused with "selected".
 */
export default function ScenarioSegmentButton({
  label,
  icon,
  value,
  meta,
  tooltip,
  open = false,
  disabled = false,
  progress = null,
  maxValueWidth = 220,
  onClick,
}: ScenarioSegmentButtonProps) {
  return (
    <Box sx={{ position: "relative" }}>
      <ButtonBase
        onClick={onClick}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{
          height: 38,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          pl: "5px",
          pr: 1.25,
          borderRadius: "8px",
          color: SB.text,
          textAlign: "left",
          background: open ? "rgba(0,212,172,0.10)" : "transparent",
          boxShadow: open ? "inset 0 0 0 1px rgba(0,212,172,0.40)" : "none",
          transition: "background 150ms ease-out, box-shadow 150ms ease-out, transform 100ms ease-out",
          "&:hover": { background: open ? "rgba(0,212,172,0.10)" : "rgba(255,255,255,0.05)" },
          "&:active": { transform: "scale(0.985)" },
          "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
          "&:focus-visible": { outline: `2px solid ${SB.accent}`, outlineOffset: 1 },
          "& .sb-tile": {
            width: 28,
            height: 28,
            borderRadius: "6px",
            background: open ? "rgba(0,212,172,0.18)" : SB.tile,
            color: open ? SB.accent : SB.textIcon,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
            transition: "background 150ms, color 150ms",
          },
          "& .sb-label": { color: open ? SB.accentSoft : SB.label, transition: "color 150ms" },
          "& .sb-chevron": {
            color: open ? SB.accent : SB.label,
            fontSize: 18,
            flex: "none",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 200ms ease-out, color 150ms",
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "& .sb-chevron": { transition: "none" },
          },
        }}
      >
        <Box component="span" className="sb-tile" aria-hidden="true">
          <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
        </Box>
        <Box
          component="span"
          sx={{ display: "flex", flexDirection: "column", gap: "1px", minWidth: 0 }}
        >
          <Typography component="span" className="sb-label" sx={labelSx}>
            {label}
          </Typography>
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: 13,
              lineHeight: "17px",
              whiteSpace: "nowrap",
            }}
          >
            <Tooltip title={tooltip ?? value} arrow>
              <Typography
                noWrap
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  lineHeight: "17px",
                  maxWidth: maxValueWidth,
                  display: "block",
                }}
              >
                {value}
              </Typography>
            </Tooltip>
            {meta && (
              <Box
                component="span"
                sx={{
                  ...tabularSx,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.625,
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: SB.meta,
                }}
              >
                {meta}
              </Box>
            )}
          </Box>
        </Box>
        <Icon className="sb-chevron" aria-hidden="true">
          expand_more
        </Icon>
      </ButtonBase>
      {progress !== null && progress !== undefined && (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            left: 42,
            right: 12,
            bottom: 1,
            height: 2,
            borderRadius: 2,
            background: "rgba(255,255,255,0.06)",
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              width: `${Math.max(0, Math.min(1, progress)) * 100}%`,
              height: 2,
              borderRadius: 2,
              background: SB.accent,
              transition: "width 400ms ease-out",
            }}
          />
        </Box>
      )}
    </Box>
  );
}
